import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Store, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";

const CATEGORIES = [
  { value: "soirees", label: "Soirée" },
  { value: "concerts", label: "Concert" },
  { value: "karaoke", label: "Karaoké" },
  { value: "humour", label: "Humour" },
  { value: "activites", label: "Activité" },
];

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

export default function PublierPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [spots, setSpots] = useState([]);
  const [form, setForm] = useState({
    title: "",
    venue: "",
    category_key: "soirees",
    neighborhood: "",
    date: "",
    time_label: "20h00",
    price: 0,
    description: "",
    image: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [myEvents, setMyEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [searchParams] = useSearchParams();

  const emptyForm = {
    title: "", venue: "", category_key: "soirees", neighborhood: "",
    date: "", time_label: "20h00", price: 0, description: "", image: "",
  };

  const loadMine = () => api.get("/my/events").then((r) => setMyEvents(r.data)).catch(() => {});

  const startEdit = (ev) => {
    setEditing(ev.slug);
    setForm({
      title: ev.title, venue: ev.venue, category_key: ev.category_key, neighborhood: ev.neighborhood,
      date: ev.date, time_label: ev.time_label, price: ev.price, description: ev.description || "", image: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const removeEvent = async (slug) => {
    if (!window.confirm(t("Retirer cet événement de GoSpot ?"))) return;
    try {
      await api.delete(`/events/${slug}`);
      toast.success(t("Événement retiré"));
      if (editing === slug) cancelEdit();
      loadMine();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    }
  };

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/establishments").then((r) => setSpots(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const editSlug = searchParams.get("edit");
    if (editSlug && user) {
      api.get("/my/events").then((r) => {
        const ev = r.data.find((e) => e.slug === editSlug);
        if (ev) startEdit(ev);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const neighborhoods = useMemo(() => [...new Set(spots.map((s) => s.neighborhood))].sort(), [spots]);
  const todayIso = new Date().toISOString().slice(0, 10);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = { ...form, price: Number(form.price) || 0, image: form.image || null };
    try {
      if (editing) {
        await api.put(`/events/${editing}`, payload);
        toast.success(t("Événement mis à jour !"));
        cancelEdit();
        loadMine();
      } else {
        const { data } = await api.post("/events", payload);
        toast.success(t("Événement publié — il est maintenant visible sur GoSpot !"));
        navigate(`/evenement/${data.slug}`);
      }
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <main className="mx-auto max-w-md px-4 py-24">
        <div className="h-64 animate-pulse rounded-3xl bg-white" />
      </main>
    );
  }

  return (
    <main data-testid="publier-page" className="mx-auto max-w-2xl px-4 pb-16 pt-10">
      <Seo title={t("Publier un événement — GoSpot")} siteName="GoSpot" description={t("Publie ton événement sur GoSpot et fais-le découvrir à des milliers de personnes à Québec.")} />

      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#2563EB]">
        <Store className="h-6 w-6 text-white" />
      </div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
        {editing ? t("Modifier l’événement") : t("Publier un événement")}
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        {t("Ton événement apparaîtra dans la page Événements et dans les suggestions GoSpot.")}
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Titre de l’événement")}</label>
          <input data-testid="pub-title" required value={form.title} onChange={set("title")} placeholder={t("Ex. : Soirée disco au Dagobert")} className={inputClass} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Lieu / établissement")}</label>
            <input data-testid="pub-venue" required value={form.venue} onChange={set("venue")} placeholder={t("Ex. : Le Dagobert")} className={inputClass} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Quartier")}</label>
            <select data-testid="pub-neighborhood" required value={form.neighborhood} onChange={set("neighborhood")} className={inputClass}>
              <option value="">{t("Choisir…")}</option>
              {neighborhoods.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Catégorie")}</label>
            <select data-testid="pub-category" value={form.category_key} onChange={set("category_key")} className={inputClass}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{t(c.label)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Prix par personne ($, 0 = gratuit)")}</label>
            <input data-testid="pub-price" type="number" min="0" step="1" value={form.price} onChange={set("price")} className={inputClass} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Date")}</label>
            <input data-testid="pub-date" type="date" required min={todayIso} value={form.date} onChange={set("date")} className={inputClass} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Heure de début")}</label>
            <input data-testid="pub-time" required value={form.time_label} onChange={set("time_label")} placeholder="20h00" className={inputClass} />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Description")}</label>
          <textarea data-testid="pub-description" rows={4} value={form.description} onChange={set("description")} placeholder={t("Décris l’ambiance, la programmation, ce qui rend ta soirée unique…")} className={inputClass} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Image (URL, optionnel)")}</label>
          <input data-testid="pub-image" type="url" value={form.image} onChange={set("image")} placeholder="https://…" className={inputClass} />
        </div>
        {error && (
          <p data-testid="pub-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
        )}
        <button
          type="submit"
          data-testid="pub-submit"
          disabled={busy}
          className="w-full rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
        >
          {busy ? t("Publication…") : editing ? t("Enregistrer les modifications") : t("Publier mon événement")}
        </button>
        {editing && (
          <button
            type="button"
            data-testid="pub-cancel-edit"
            onClick={cancelEdit}
            className="w-full rounded-full border border-slate-200 py-3 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
          >
            {t("Annuler la modification")}
          </button>
        )}
      </form>

      {myEvents.length > 0 && (
        <section className="mt-10" data-testid="my-events-section">
          <h2 className="text-lg font-bold text-slate-900">{t("Mes événements publiés")}</h2>
          <div className="mt-4 space-y-3">
            {myEvents.map((ev) => (
              <div
                key={ev.slug}
                data-testid={`my-event-${ev.slug}`}
                className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3"
              >
                <img src={ev.image} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{ev.title}</p>
                  <p className="text-xs text-slate-400">
                    {ev.date} • {ev.time_label} • {t(ev.category_label)}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`edit-event-${ev.slug}`}
                  onClick={() => startEdit(ev)}
                  aria-label={t("Modifier")}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-blue-300 hover:text-[#2563EB]"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  data-testid={`delete-event-${ev.slug}`}
                  onClick={() => removeEvent(ev.slug)}
                  aria-label={t("Retirer")}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-red-300 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
